import React from "react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";
import { ROUTE_LABELS } from "@/lib/personalization";
import TrustSignal from "@/components/TrustSignal";

const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };

// Publication rule: approved stories are public. Demo (fictional) stories render only while the CMS toggle demo_preview === "on",
// and are always labelled as examples. Drafts never render.
export const isApproved = (s) => s.status === "approved" || (!s.status && s.permission === "approved");
export const isDemo = (s) => s.status === "demo";

export default function RealStories() {
  const { section } = useSiteContent();
  const c = section("real_stories", HOME.real_stories);
  const all = Array.isArray(c.stories) ? c.stories : [];
  const previewOn = c.demo_preview === "on";
  const stories = all.filter((s) => (isApproved(s) || (previewOn && isDemo(s))) && (s.decision || s.reality || s.changed || s.quote));
  const anyDemo = stories.some(isDemo);

  return (
    <div data-testid="real-stories" data-count={stories.length} data-demo={anyDemo ? "true" : "false"} className="mt-20 md:mt-28 border-t border-[#451C25]/15 pt-12 md:pt-16">
      <div className="max-w-3xl">
        <p className="text-[#897756] text-[11px] tracking-[0.3em] uppercase mb-5 font-semibold" style={K}>{c.eyebrow}</p>
        <h3 data-testid="real-stories-headline" className="text-3xl md:text-4xl leading-[1.05] tracking-tight" style={{ ...K, fontWeight: 600 }}>{c.headline}</h3>
        <TrustSignal className="mt-5" />
        <p className="mt-5 text-[#6B4954] text-base md:text-lg leading-relaxed" style={S}>{c.intro}</p>
        {anyDemo && c.demo_note && (
          <p data-testid="real-stories-demo-note" className="mt-4 text-[#897756] text-[13px] italic" style={S}>{c.demo_note}</p>
        )}
      </div>

      {stories.length === 0 ? (
        <p data-testid="real-stories-empty" className="mt-8 max-w-2xl text-[#6B4954] text-[15px] italic leading-relaxed border-l-2 border-[#B3A17D] pl-4" style={S}>{c.empty_note}</p>
      ) : (
        <ul data-testid="real-stories-list" className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-14">
          {stories.map((s, i) => <Story key={s.id || i} s={s} c={c} index={i} />)}
        </ul>
      )}
    </div>
  );
}

function Story({ s, c, index }) {
  const demo = isDemo(s);
  const clientPhoto = !demo && s.photo_is_client === "yes";
  const routeLabel = ROUTE_LABELS[s.route] || s.route;
  const path = [s.origin, s.destination].filter(Boolean).join(" → ");
  return (
    <li data-testid={`real-story-${index + 1}`} data-status={s.status || (s.permission === "approved" ? "approved" : "")} className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-6 sm:gap-8">
      {s.photo && (
        <figure className="hidden sm:block">
          <img src={s.photo} alt={clientPhoto ? s.name : s.photo_alt || ""} loading="lazy" className="w-full aspect-[4/5] object-cover" />
          {!clientPhoto && <figcaption className="mt-2 text-[10px] tracking-[0.18em] uppercase text-[#897756]" style={K}>{c.illustrative_label}</figcaption>}
        </figure>
      )}
      <div className="min-w-0">
        {demo && (
          <p data-testid={`real-story-${index + 1}-demo-label`} className="text-[10px] tracking-[0.26em] uppercase font-semibold text-[#897756] border border-[#B3A17D]/60 inline-block px-2 py-1 mb-4" style={K}>{c.demo_label}</p>
        )}
        {[["decision", c.decision_label], ["reality", c.reality_label], ["changed", c.changed_label]].map(([k, label]) => s[k] ? (
          <div key={k} className="mt-3 first:mt-0">
            <p className="text-[#897756] text-[10px] tracking-[0.26em] uppercase font-semibold" style={K}>{label}</p>
            <p className="mt-1 text-[#451C25] text-[15px] leading-relaxed" style={S}>{s[k]}</p>
          </div>
        ) : null)}
        {s.quote && (
          <blockquote className="mt-5 border-l-2 border-[#B3A17D] pl-4 text-[#451C25] text-[17px] md:text-lg italic leading-snug" style={S}>{s.quote}</blockquote>
        )}
        <p className="mt-4 text-[12px] text-[#6B4954]" style={K}>
          <span className="text-[#451C25] font-semibold">{s.name}</span>
          {path ? <span className="block mt-0.5">{path}</span> : null}
          {routeLabel ? <span className="block mt-0.5 text-[#897756] tracking-[0.12em] uppercase text-[10px]">{routeLabel}</span> : null}
          {!demo && isApproved(s) && c.permission_label ? <span className="block mt-1 text-[11px] italic" style={S}>{c.permission_label}</span> : null}
        </p>
      </div>
    </li>
  );
}
